/* eslint-disable max-lines,max-lines-per-function */

import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';

import { distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';

import { TimeService, trackByProp } from '@recruitee/common';
import { InfoboxComponent } from '@recruitee/components/infobox';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { OpeningAssignment } from '@recruitee/offers-types';
import type { PlacementWithoutOpening } from '@recruitee/placements-types';
import type { JobRequisition, RequisitionOpening } from '@recruitee/requisitions-types';
import { RequisitionOpeningReason } from '@recruitee/requisitions-types';
import {
  AvatarComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogData,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  HeadingComponent,
  IconComponent,
  InputComponent,
  SelectComponent,
  SelectOption,
  TruncateComponent,
} from '@recruitee/user-interface';
import { LifeCycle } from '@recruitee/utils';

type AssignHiresMultiRequisitionsDialogData = {
  placements: PlacementWithoutOpening[];
  requisitions: JobRequisition[];
  saveHandler$: (assignmentsByRequisitionId: {
    [key: number]: OpeningAssignment[];
  }) => Observable<null>;
};

interface PlacementValue {
  requisitionId: number | null;
  openingId: number | null;
}

type PlacementFormsByPlacementId = {
  [key: number]: FormGroup<{
    requisitionId: FormControl;
    openingId: FormControl;
  }>;
};
type OptionsMap = Map<number, SelectOption[]>;
type OpeningsMap = Map<number, RequisitionOpening[]>;
type AutoAssignOpeningsMap = Map<number, boolean>;
type SimpleOpeningsMap = Map<number, boolean>;
type OpeningLabelsMap = Map<number, Map<number, string>>;

function getPlacementAssignmentsValidator(openingsCount: number): ValidatorFn {
  return (form: FormGroup) => {
    const placementValues: PlacementValue[] = Object.values(form.value);
    const allCandidatesAssignedToOpening = placementValues.every(
      placementValue => placementValue.openingId && placementValue.requisitionId,
    );
    const allAvailableOpeningsSelected =
      placementValues.filter(
        placementValue => placementValue.openingId && placementValue.requisitionId,
      ).length === openingsCount;

    if (allCandidatesAssignedToOpening || allAvailableOpeningsSelected) return null;
    return { openingsNotAssigned: true };
  };
}

@Component({
  selector: 'rt-requisitions-assign-hires-multi-requisitions-dialog',
  standalone: true,
  imports: [
    AvatarComponent,
    ButtonComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    HeadingComponent,
    IconComponent,
    InputComponent,
    SelectComponent,
    TruncateComponent,
    TranslatePipe,
    ReactiveFormsModule,
    InfoboxComponent,
  ],
  templateUrl: './assign-hires-multi-requisitions-dialog.component.html',
  styleUrls: ['./assign-hires-multi-requisitions-dialog.component.less'],
})
export class AssignHiresMultiRequisitionsDialogComponent
  implements DialogComponentI<AssignHiresMultiRequisitionsDialogData, void>
{
  private readonly openingReasonText: Record<RequisitionOpeningReason, string> = {
    [RequisitionOpeningReason.NewPosition]: this.i18n.translate(
      'app.requisitions.openings.reason.new_position',
    ),
    [RequisitionOpeningReason.Replacement]: this.i18n.translate(
      'app.requisitions.openings.reason.replacement',
    ),
  };
  public readonly texts = {
    openOn: (date: string) => this.i18n.translate('app.requisitions.openings.opens_on', { date }),
    select: this.i18n.translate('app.common.select'),
    automaticallyAssigned: this.i18n.translate(
      'app.requisitions.assign_hires_dialog.automatically_assigned_label',
    ),
    notAssigned: this.i18n.translate('app.requisitions.assign_hires_dialog.not_assigned_label'),
    manuallyAssigned: this.i18n.translate(
      'app.requisitions.assign_hires_dialog.manually_assigned_label',
    ),
  };

  public form: FormGroup;

  public trackById = trackByProp<{ id: number }>({ prop: 'id' });
  public placements: PlacementWithoutOpening[] = [];

  public requisitions: JobRequisition[] = [];
  public availableRequisitions: JobRequisition[] = [];
  public autoRequisitionsAssign = false;

  public openingsByRequisitionId: OpeningsMap = new Map();
  public availableOpeningsByRequisitionId: OpeningsMap = new Map();
  public autoAssignOpeningsByPlacementId: AutoAssignOpeningsMap = new Map();
  public openingsNumber = 0;
  public availableOpeningsNumber = 0;
  public notEnoughOpenings = false;

  public openingOptionsByPlacementId: OptionsMap = new Map();
  public requisitionOptionsByPlacementId: OptionsMap = new Map();

  private openingLabelsByRequisitionId: OpeningLabelsMap = new Map();
  private simpleOpeningsByRequisitionId: SimpleOpeningsMap = new Map();

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private dialogRef: DialogRef,
    private cdRef: ChangeDetectorRef,
    private fb: FormBuilder,
    private i18n: I18nService,
    private timeService: TimeService,
    @Inject(DialogData) private data: AssignHiresMultiRequisitionsDialogData,
  ) {}

  public setDialogData(data: AssignHiresMultiRequisitionsDialogData): void {
    this.setUpBasicData(data);

    this.buildForm();
    this.updateDataOnFormChanges();

    this.autofillOpenings();

    this.buildLabels();
    this.buildRequisitionOptions();
    this.buildOpeningOptions();
  }

  public getOpeningControl(placementId: number): FormControl {
    return <FormControl>this.form.get(`${placementId}.openingId`);
  }

  public getRequisitionControl(placementId: number): FormControl {
    return <FormControl>this.form.get(`${placementId}.requisitionId`);
  }

  public save() {
    const formValue: { [key: number]: PlacementValue } = this.form.getRawValue();

    const assignmentsByRequisitionId = Object.entries(formValue).reduce(
      (acc, [key, value]) => {
        if (!value.requisitionId || !value.openingId) return acc;

        const requisitionAssignments = acc[value.requisitionId] || [];
        requisitionAssignments.push({ placementId: parseInt(key, 10), openingId: value.openingId });

        return {
          ...acc,
          [value.requisitionId]: requisitionAssignments,
        };
      },
      <{ [key: number]: OpeningAssignment[] }>{},
    );

    this.data
      .saveHandler$(assignmentsByRequisitionId)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.dialogRef.close());
  }

  public cancel() {
    this.dialogRef.close();
  }

  private setUpBasicData(data: AssignHiresMultiRequisitionsDialogData): void {
    this.placements = data.placements;

    this.requisitions = data.requisitions.sort((a, b) => a.title.localeCompare(b.title));
    this.availableRequisitions = this.requisitions;
    this.autoRequisitionsAssign = data.requisitions.length <= 1;

    this.setOpenings();
    this.setSimpleOpeningsMap();
    this.setNotEnoughOpenings();
    this.openingsNumber = this.requisitions.reduce((acc, cur) => acc + cur.openings.length, 0);
  }

  private setOpenings() {
    this.openingsByRequisitionId = new Map<number, RequisitionOpening[]>();
    this.availableOpeningsByRequisitionId = new Map<number, RequisitionOpening[]>();

    this.requisitions.forEach(requisition => {
      const sortedOpenings = this.sortOpenings([...requisition.openings], requisition.id);
      this.openingsByRequisitionId.set(requisition.id, sortedOpenings);
      this.availableOpeningsByRequisitionId.set(requisition.id, sortedOpenings);
    });
  }

  private sortOpenings(opening: RequisitionOpening[], requisitionId: number): RequisitionOpening[] {
    return opening.sort((opening1, opening2) => {
      if (
        opening1.reason &&
        opening2.reason &&
        opening1.anticipatedStartDate &&
        opening2.anticipatedStartDate
      ) {
        const opening1ReasonText = this.getReasonText(opening1);
        const opening2ReasonText = this.getReasonText(opening2);
        const comparisonResult = this.compareDates(
          opening1.anticipatedStartDate,
          opening2.anticipatedStartDate,
        );
        return comparisonResult === 0
          ? opening1ReasonText.localeCompare(opening2ReasonText)
          : comparisonResult;
      }

      if (opening1.anticipatedStartDate && opening2.anticipatedStartDate) {
        return this.compareDates(opening1.anticipatedStartDate, opening2.anticipatedStartDate);
      }

      return this.getLabel(opening1, requisitionId).localeCompare(
        this.getLabel(opening2, requisitionId),
      );
    });
  }

  private compareDates(testDate: string, targetDate: string): number {
    return new Date(testDate).valueOf() - new Date(targetDate).valueOf();
  }

  private getReasonText(opening: RequisitionOpening): string {
    return opening.reason ? this.openingReasonText[opening.reason] : '';
  }

  private setNotEnoughOpenings(): void {
    const openingsNumber = this.requisitions.reduce((acc, cur) => acc + cur.openings.length, 0);
    this.notEnoughOpenings = this.placements.length > openingsNumber;
  }

  private setSimpleOpeningsMap(): void {
    this.simpleOpeningsByRequisitionId = new Map<number, boolean>();

    this.requisitions.forEach(requisition => {
      const simpleOpenings = requisition.openings.some(
        opening => !opening.reason && !opening.anticipatedStartDate,
      );
      this.simpleOpeningsByRequisitionId.set(requisition.id, simpleOpenings);
    });
  }

  private buildForm() {
    const placementForms = this.placements.reduce(
      (acc, placement) => {
        const requisitionIdControl = new FormControl<number | string | null>(
          this.autoRequisitionsAssign ? this.requisitions[0].id : null,
        );
        const openingIdControl = new FormControl<number | string | null>(null);

        return {
          ...acc,
          [placement.id]: new FormGroup({
            requisitionId: requisitionIdControl,
            openingId: openingIdControl,
          }),
        };
      },
      <PlacementFormsByPlacementId>{},
    );
    this.form = new FormGroup(
      placementForms,
      getPlacementAssignmentsValidator(this.openingsNumber),
    );
  }

  private updateDataOnFormChanges() {
    this.resetOpeningOnRequisitionChanges();
    this.rebuildOptionsAndAutofillOpenings();
  }

  private resetOpeningOnRequisitionChanges() {
    this.placements.forEach(placement => {
      const requisitionIdControl = this.getRequisitionControl(placement.id);
      requisitionIdControl.valueChanges
        .pipe(distinctUntilChanged(), takeUntil(this.ngOnDestroy$))
        .subscribe(() => {
          this.getOpeningControl(placement.id).reset();
        });
    });
  }

  private rebuildOptionsAndAutofillOpenings() {
    this.form.valueChanges.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      const selectedOpeningIds = this.getSelectedOpeningIds();
      const selectedRequisitionIds = this.getSelectedRequisitionIds();

      this.availableOpeningsByRequisitionId = new Map<number, RequisitionOpening[]>();
      this.requisitions.forEach(requisition => {
        this.availableOpeningsByRequisitionId.set(
          requisition.id,
          requisition.openings.filter(opening => !selectedOpeningIds.includes(opening.id)),
        );
      });

      this.availableRequisitions = this.requisitions.filter(
        requisition =>
          !(
            selectedRequisitionIds.includes(requisition.id) &&
            !this.availableOpeningsByRequisitionId.get(requisition.id)?.length
          ),
      );

      this.buildRequisitionOptions();
      this.buildOpeningOptions();
      this.autofillOpenings();
      this.cdRef.detectChanges();
    });
  }

  private getSelectedOpeningIds() {
    return <number[]>Object.values(
      <Record<number, { openingId: number | string | null }>>this.form.getRawValue(),
    )
      .filter(value => !!value.openingId)
      .map(value => value.openingId);
  }

  private getSelectedRequisitionIds() {
    return <number[]>Object.values(
      <Record<number, { requisitionId: number | string | null }>>this.form.getRawValue(),
    )
      .filter(value => !!value.requisitionId)
      .map(value => value.requisitionId);
  }

  private autofillOpenings() {
    let autofillUsed = false;
    let openingIndex = 0;

    this.placements.forEach(placement => {
      const requisitionId = this.getRequisitionControl(placement.id).value;
      const openingControl = this.getOpeningControl(placement.id);
      const openingId = openingControl.value;
      const requisitionAvailableOpenings =
        this.availableOpeningsByRequisitionId.get(requisitionId)!;
      const isOpeningAvailable = requisitionId ? requisitionAvailableOpenings[openingIndex] : false;

      if (!isOpeningAvailable || openingId) {
        const autoAssign = openingId
          ? !!this.autoAssignOpeningsByPlacementId.get(placement.id)
          : false;
        this.autoAssignOpeningsByPlacementId.set(placement.id, autoAssign);
        return;
      }

      const newOpeningValue = requisitionAvailableOpenings[openingIndex]?.id || null;
      const autoAssign = this.autoRequisitionsAssign
        ? !this.notEnoughOpenings && !!this.simpleOpeningsByRequisitionId.get(requisitionId)
        : !!this.simpleOpeningsByRequisitionId.get(requisitionId);
      this.autoAssignOpeningsByPlacementId.set(placement.id, autoAssign);

      if (!autoAssign) return;

      openingControl.patchValue(newOpeningValue, {
        emitEvent: false,
      });
      autofillUsed = true;
      openingIndex++;
    });

    if (!autofillUsed) return;

    this.form.updateValueAndValidity();
  }

  private buildLabels() {
    this.openingLabelsByRequisitionId = new Map<number, Map<number, string>>();
    this.requisitions.forEach(requisition => {
      const openingLabels = new Map<number, string>();
      requisition.openings.forEach(opening => {
        openingLabels.set(opening.id, this.getLabel(opening, requisition.id));
      });
      this.openingLabelsByRequisitionId.set(requisition.id, openingLabels);
    });
  }

  private getLabel(opening: RequisitionOpening, requisitionId: number) {
    const reasonText = opening.reason ? this.openingReasonText[opening.reason] : '';
    const dateText = opening.anticipatedStartDate
      ? this.texts.openOn(
          this.timeService.format({
            input: opening.anticipatedStartDate,
            pattern: 'DD MMM YYYY',
          }),
        )
      : '';

    if (reasonText && dateText) return `${reasonText}: ${dateText}`;
    if (reasonText) return reasonText;
    if (dateText) return dateText;

    const autoAssign = this.autoRequisitionsAssign
      ? !this.notEnoughOpenings
      : !!this.simpleOpeningsByRequisitionId.get(requisitionId);

    return autoAssign ? this.texts.automaticallyAssigned : this.texts.manuallyAssigned;
  }

  private buildRequisitionOptions() {
    this.requisitionOptionsByPlacementId = new Map<number, SelectOption[]>();
    const formValue: { [key: number]: PlacementValue } = this.form.getRawValue();

    this.placements.forEach(placement => {
      const options: SelectOption[] = [];
      const selectedRequisitionId = formValue[placement.id].requisitionId;
      const requisitionsForOptions = this.requisitions.filter(
        opening =>
          this.availableRequisitions.includes(opening) || selectedRequisitionId === opening.id,
      );

      requisitionsForOptions.forEach(requisition => {
        options.push({
          label: requisition.title,
          value: requisition.id,
        });
      });

      this.requisitionOptionsByPlacementId.set(placement.id, options);
    });
  }

  private buildOpeningOptions() {
    this.openingOptionsByPlacementId = new Map<number, SelectOption[]>();
    const formValue: { [key: number]: PlacementValue } = this.form.getRawValue();

    this.placements.forEach(placement => {
      const options: SelectOption[] = [];
      const selectedRequisitionId = formValue[placement.id].requisitionId;

      if (!selectedRequisitionId) {
        this.openingOptionsByPlacementId.set(placement.id, options);
        return;
      }

      const selectedOpeningId = formValue[placement.id].openingId;
      const openingsLabel = this.openingLabelsByRequisitionId.get(selectedRequisitionId)!;
      const selectedOpeningLabel = openingsLabel.get(selectedOpeningId!) || '';
      const openingsForOptions = this.openingsByRequisitionId
        .get(selectedRequisitionId)!
        .filter(
          opening =>
            this.availableOpeningsByRequisitionId.get(selectedRequisitionId)?.includes(opening) ||
            selectedOpeningId === opening.id,
        );
      const presentLabels = new Set<string>([]);

      openingsForOptions.forEach(opening => {
        const label = openingsLabel.get(opening.id) || '';

        if (
          presentLabels.has(label) ||
          (label === selectedOpeningLabel && selectedOpeningId !== opening.id)
        )
          return;

        presentLabels.add(label);
        options.push({
          label,
          value: opening.id,
        });
      });

      this.openingOptionsByPlacementId.set(placement.id, options);
    });

    this.updateAvailableOpeningsNumber();
  }

  private updateAvailableOpeningsNumber() {
    let availableOpeningsNumber = 0;
    this.requisitions.forEach(requisition => {
      const openingsLength = (this.availableOpeningsByRequisitionId.get(requisition.id) || [])
        .length;
      availableOpeningsNumber += openingsLength;
    });

    this.availableOpeningsNumber = availableOpeningsNumber;
  }
}
